/**
 * Lazy, memoised data loads (OMON-281).
 *
 * THE RULE: boot does not fetch data on behalf of routes. It signs the user
 * in, starts the router, and lets each route await exactly what it needs.
 *
 * Why this exists. `main.ts loadData()` used to `await Promise.all([...])`
 * over the whole catalogue AND the patient list before calling `applyRoute`,
 * so every route paid for ~690 catalogue documents (~828 KB) whether or not
 * it read one of them. On desktop that's a second of fast-LAN round trips and
 * invisible; measured on an iPhone over Safari Web Inspector (2026-07-31) it
 * put the Connect swimlane's own data call — 4.05s, and the FASTEST thing in
 * the trace — behind ~2.5 minutes of queueing. Mobile did not introduce the
 * cost, it removed the margin that hid it.
 *
 * Each `ensure*` returns a promise that resolves once its slice of `state` is
 * populated, and is memoised so concurrent callers share one fetch. A failed
 * load clears the memo so the next caller retries rather than inheriting a
 * permanently-rejected promise.
 *
 * Sign-out must call `resetEnsured()` (see main.ts stopListeners) or the next
 * user would read the previous one's cached catalogue.
 *
 * ── Plans load only their own definitions (2026-09-17) ──────────────────
 *
 * OMON-281 moved the WHOLE catalogue off boot and onto the first plan open,
 * which made the first plan of a session pay for all of it: over a minute on
 * a phone, then every later plan instant (Ed, 2026-09-17). A plan references
 * a handful of definitions, so the plan path now awaits only:
 *
 *   - `ensureHierarchy()` — goal categories + sub-categories (tens of docs);
 *   - `ensureDefinitions(codes)` — the observation definitions it names.
 *
 * and then `prefetchCatalogueSoon()` starts the full catalogue in the
 * background once the plan has rendered, so the builder and inventory are
 * usually ready by the time anyone opens them.
 *
 * ☠️ The builder, the draft view and the catalogue panel still need the FULL
 * catalogue (the builder walks every definition). Their entry points await
 * `ensureCatalogue()` — they must never assume the plan view loaded it.
 *
 * ☠️ Maps are filled IN PLACE, never replaced. A plan load captures
 * `state.catalogueData` at its start; if the background load swapped in a new
 * Map mid-load, the targeted definitions would land in one map while the plan
 * read from the other.
 */

import { state } from "../state";
import {
  loadCatalogue,
  loadActivities,
  loadHierarchy,
  loadDefinitionsByIds,
  subscribeCatalogue,
  subscribeActivities,
  subscribeHierarchy,
  type LiveUpdate,
} from "./catalogue";
import { loadPatients } from "./patients";
import { applyLiveChanges, fillInPlace } from "./live-maps";

let cataloguePromise: Promise<void> | null = null;
let hierarchyPromise: Promise<void> | null = null;
let patientsPromise: Promise<void> | null = null;
/** True once the full catalogue has loaded — targeted fetches become no-ops. */
let catalogueComplete = false;
/** Definition ids already fetched (found or not), so a plan reopened does not refetch. */
const definitionsTried = new Set<string>();
/** Definition ids being fetched right now → that fetch, so a concurrent caller waits for it. */
const definitionsInFlight = new Map<string, Promise<void>>();
/** Memo for `ensureCatalogueLive` — the listeners are attached once per session. */
let livePromise: Promise<void> | null = null;
/** Unsubscribe handles for the live catalogue listeners (empty when detached). */
let liveUnsubs: Array<() => void> = [];
/** Bumped by `stopCatalogueLive` so an attach still awaiting a preload stands down. */
let liveGeneration = 0;
/** Rejects an attach whose first snapshots have not all arrived (sign-out mid-load). */
let abortPendingLive: (() => void) | null = null;

/**
 * Populate `state.catalogueData`, `state.activityData` and the goal hierarchy.
 *
 * Needed by: the Goals Inventory panel, the plan builder, and the draft view.
 * NOT needed by: the dashboard, the clients LIST, a plan's read-only view
 * (see `ensureDefinitions`), or anything under Connect.
 */
export function ensureCatalogue(): Promise<void> {
  if (!cataloguePromise) {
    cataloguePromise = (async () => {
      const [cat, act, hier] = await Promise.all([
        loadCatalogue(),
        loadActivities(),
        loadHierarchy(),
      ]);
      fillInPlace(state.catalogueData, cat);
      fillInPlace(state.activityData, act);
      fillInPlace(state.goalCategories, hier.categories);
      fillInPlace(state.goalSubcategories, hier.subcategories);
      catalogueComplete = true;
    })().catch((err) => {
      cataloguePromise = null; // let the next caller retry
      throw err;
    });
  }
  return cataloguePromise;
}

/** Whether the full catalogue is already in `state` (no wait needed). */
export function isCatalogueComplete(): boolean {
  return catalogueComplete;
}

/**
 * Keep the catalogue LIVE (OMON-102) — attach the four listeners once per
 * session and leave them attached until sign-out (`resetEnsured`).
 *
 * `onRemoteChange` is called after a snapshot changes the state Maps, so the
 * Goals Inventory can re-render (the caller coalesces and gates it). It is
 * captured on the first call only.
 *
 * Two ways in, so the ~690 documents are fetched once, not twice:
 *   - the catalogue was never requested → the listeners' first snapshots ARE
 *     the load: this promise stands in for `ensureCatalogue()` (other callers
 *     share it) and resolves when all four have arrived. No render is
 *     triggered for them — the caller renders once on resolve.
 *   - the catalogue was already loaded / loading (plan prefetch, builder) →
 *     wait for that, then attach; the listeners only apply changes. Their
 *     first snapshots still refill the Maps in place (they may be newer than
 *     the one-shot load) and trigger ONE render when all have arrived.
 *
 * ☠️ Maps are filled in place, never replaced (see header).
 */
export function ensureCatalogueLive(onRemoteChange: () => void): Promise<void> {
  if (livePromise) return livePromise;
  const preloaded = cataloguePromise;
  const generation = liveGeneration;
  const promise: Promise<void> = (async () => {
    if (preloaded) {
      await preloaded;
      if (generation !== liveGeneration) return; // signed out meanwhile
      // Data is already in state: attach and return without waiting for the
      // first snapshots. A late first-snapshot failure is logged, and the
      // memo cleared so the next visit re-attaches.
      attachLiveListeners(true, onRemoteChange).catch((err) => {
        console.error("Catalogue live listeners failed:", err);
        stopCatalogueLive();
      });
      return;
    }
    await attachLiveListeners(false, onRemoteChange);
  })().catch((err) => {
    stopCatalogueLive();
    if (cataloguePromise === promise) cataloguePromise = null;
    throw err;
  });
  livePromise = promise;
  if (!preloaded) cataloguePromise = promise;
  return promise;
}

/** Detach the live catalogue listeners and drop the memo. */
export function stopCatalogueLive(): void {
  liveGeneration += 1;
  liveUnsubs.forEach((u) => u());
  liveUnsubs = [];
  livePromise = null;
  abortPendingLive?.();
  abortPendingLive = null;
}

/** Whether the live catalogue listeners are currently attached. */
export function isCatalogueLive(): boolean {
  return liveUnsubs.length > 0;
}

/**
 * Attach the listeners; resolves once all first snapshots have landed (which
 * also marks the catalogue complete). `notifyOnFirst` decides whether that
 * moment triggers `onRemoteChange` (only when the data was already on screen).
 */
function attachLiveListeners(notifyOnFirst: boolean, onRemoteChange: () => void): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let pendingFirst = 3;
    let settled = false;
    abortPendingLive = () => {
      if (!settled) {
        settled = true;
        reject(new Error("catalogue live listeners stopped before first load"));
      }
    };
    const firstArrived = () => {
      pendingFirst -= 1;
      if (pendingFirst > 0) return;
      catalogueComplete = true;
      settled = true;
      abortPendingLive = null;
      resolve();
      if (notifyOnFirst) onRemoteChange();
    };
    const onError = (err: Error) => {
      if (!settled) {
        settled = true;
        reject(err);
      } else {
        // A Firestore listener that errors is dead. Detach the set so the
        // next visit to the tab re-attaches (the Maps keep their data).
        console.error("Catalogue live listener error:", err);
        stopCatalogueLive();
      }
    };
    const keyed = <V>(target: () => Map<string, V>) => {
      let sawFirst = false;
      return (u: LiveUpdate<V>) => {
        if (u.first) {
          fillInPlace(target(), u.entries);
          if (!sawFirst) {
            sawFirst = true;
            firstArrived();
          }
          return;
        }
        if (applyLiveChanges(target(), u.changes) && settled) onRemoteChange();
      };
    };
    let hierFirst = true;
    liveUnsubs = [
      subscribeCatalogue(keyed(() => state.catalogueData), onError),
      subscribeActivities(keyed(() => state.activityData), onError),
      subscribeHierarchy(({ categories, subcategories }) => {
        fillInPlace(state.goalCategories, categories);
        fillInPlace(state.goalSubcategories, subcategories);
        if (hierFirst) {
          hierFirst = false;
          firstArrived();
        } else if (settled) {
          onRemoteChange();
        }
      }, onError),
    ];
  });
}

/**
 * Populate the goal category + sub-category maps only. Small, and every plan
 * needs both to label its goals.
 */
export function ensureHierarchy(): Promise<void> {
  if (catalogueComplete) return Promise.resolve();
  if (!hierarchyPromise) {
    hierarchyPromise = (async () => {
      const hier = await loadHierarchy();
      if (catalogueComplete) return; // the full load won the race — leave it
      fillInPlace(state.goalCategories, hier.categories);
      fillInPlace(state.goalSubcategories, hier.subcategories);
    })().catch((err) => {
      hierarchyPromise = null;
      throw err;
    });
  }
  return hierarchyPromise;
}

/**
 * Make sure the named observation definitions are in `state.catalogueData`.
 *
 * Adds to the map; never removes. Ids already present, or already tried, are
 * skipped — a definition that is missing or inactive stays absent, exactly as
 * it would be after the full load.
 */
export async function ensureDefinitions(codes: Iterable<string>): Promise<void> {
  if (catalogueComplete) return;
  const unique = [...new Set(codes)].filter((c) => c && !state.catalogueData.has(c));
  // ☠️ OMON-351: an id another call is STILL fetching must be waited for, not
  // skipped. A client open now fetches the plan's definitions and the
  // narrative's concurrently, and they share ids; were "already tried" to
  // return early for an id whose fetch had not landed, the plan would render
  // "(unknown monitored event …)" for a definition that exists.
  const waits: Promise<void>[] = [];
  const wanted: string[] = [];
  for (const c of unique) {
    const pending = definitionsInFlight.get(c);
    if (pending) waits.push(pending);
    else if (!definitionsTried.has(c)) wanted.push(c);
  }
  if (wanted.length > 0) {
    wanted.forEach((c) => definitionsTried.add(c));
    // Started on a microtask so `request` is assigned before its body can finish.
    const request: Promise<void> = Promise.resolve().then(async () => {
      try {
        const found = await loadDefinitionsByIds(wanted);
        if (catalogueComplete) return; // the full load already has them
        found.forEach((v, k) => state.catalogueData.set(k, v));
      } catch (err) {
        wanted.forEach((c) => definitionsTried.delete(c)); // retry next time
        throw err;
      } finally {
        wanted.forEach((c) => {
          if (definitionsInFlight.get(c) === request) definitionsInFlight.delete(c);
        });
      }
    });
    wanted.forEach((c) => definitionsInFlight.set(c, request));
    waits.push(request);
  }
  await Promise.all(waits);
}

/**
 * Start loading the full catalogue once the page is idle.
 *
 * Deliberately NOT at the same moment as a plan's own reads: on a phone the
 * full catalogue queued everything behind it for minutes (see the header), so
 * starting it alongside the targeted fetch would give the time straight back.
 * A failure is swallowed here — whoever next needs the catalogue retries it.
 */
export function prefetchCatalogueSoon(): void {
  if (catalogueComplete || cataloguePromise) return;
  const start = () => {
    ensureCatalogue().catch(() => {});
  };
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
    .requestIdleCallback;
  if (typeof ric === "function") ric(start, { timeout: 3000 });
  else setTimeout(start, 1000);
}

// ── The client list (OMON-351) ─────────────────────────────────────────────
//
// The list used to be re-read IN FULL, and waited for, on every Plans-tab
// click, every Connect entry and every deep link, plus a discarded re-read
// after each publish / discard. It is now read once and kept current:
//
//   - every plan open rebuilds that client's row from the reads it already
//     makes (loadPatientPlan → `row`, merged by main.ts), which covers this
//     session's own publish / discard / restore;
//   - saveDraft marks the row when it creates a draft;
//   - entering Plans or Connect with a list older than PATIENT_LIST_FRESH_MS
//     re-reads it — in the background for Plans (the cached list renders at
//     once), awaited for Connect (its IoT sweep runs over the list).
//
// ☠️ The OMON-035 staleness guard is kept, not dropped: its stated cause was a
// publish/discard in THIS session leaving stale pills, which the row refresh
// now fixes at the source (and more reliably — the old post-publish re-read
// threw its result away). Other people's changes still arrive, at most
// PATIENT_LIST_FRESH_MS late, on the next entry.
//
// ☠️ "Loaded" is a flag, not `state.patients.length > 0`: an empty list is a
// real answer (a scoped user with no clients) and must not re-read forever.

/** How long a full client-list read counts as current. */
export const PATIENT_LIST_FRESH_MS = 60_000;
/** When the full list last landed in `state.patients` (0 = never this session). */
let patientsLoadedAt = 0;
/** Bumped by `resetEnsured` so a read in flight at sign-out cannot land. */
let patientsGeneration = 0;

/**
 * Re-read the full client list into `state.patients`. Concurrent callers share
 * one read; a failure clears the memo so the next caller retries.
 */
export function reloadPatients(): Promise<void> {
  if (!patientsPromise) {
    const generation = patientsGeneration;
    const p: Promise<void> = loadPatients().then((list) => {
      if (generation !== patientsGeneration) return; // signed out meanwhile
      state.patients = list;
      patientsLoadedAt = Date.now();
    }).finally(() => {
      if (patientsPromise === p) patientsPromise = null;
    });
    patientsPromise = p;
  }
  return patientsPromise;
}

/** Whether the full list has been read this session (it may be empty). */
export function isPatientListLoaded(): boolean {
  return patientsLoadedAt > 0;
}

/** Whether the full list was read within PATIENT_LIST_FRESH_MS of `now`. */
export function isPatientListFresh(now: number = Date.now()): boolean {
  return patientsLoadedAt > 0 && now - patientsLoadedAt < PATIENT_LIST_FRESH_MS;
}

/**
 * The full list, read once: a no-op once it has loaded this session. For the
 * paths that need the whole list but not a fresh one (the Users & roles
 * catalogue). A client deep link does NOT need it — see router.ts.
 */
export function ensurePatients(): Promise<void> {
  return isPatientListLoaded() ? Promise.resolve() : reloadPatients();
}

/** The full list, re-read first if it is older than PATIENT_LIST_FRESH_MS. */
export function ensureFreshPatients(): Promise<void> {
  return isPatientListFresh() ? Promise.resolve() : reloadPatients();
}

/** Drop the memos so a subsequent sign-in re-fetches. Called on sign-out. */
export function resetEnsured(): void {
  // The live catalogue listeners outlive the tab but not the session: the
  // next user must not inherit them (nor their permission errors).
  stopCatalogueLive();
  cataloguePromise = null;
  hierarchyPromise = null;
  patientsPromise = null;
  patientsLoadedAt = 0;
  patientsGeneration += 1;
  catalogueComplete = false;
  definitionsTried.clear();
  definitionsInFlight.clear();
}
